import React, { Fragment, useState, useEffect } from 'react';
import TableBuilder, { ApiRequest, ButtonBuilder, ToastBuilder, HoverOver, ActionGroupBuilder } from '../../../../../../../GlobalComponents/Builders';
import { Row, Col, CardHeader, CardBody, Card } from 'reactstrap';
import { H4 } from '../../../../../../../AbstractElements';

const STATUS_COLORS = {
    Approved: '#198754',
    Pending: '#ffc107',
};

const KeyResourceMappings = () => {
    const [mappings, setMappings] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);
    const [processingRows, setProcessingRows] = useState({});

    useEffect(() => { loadMappings(); }, []);

    const loadMappings = () => {
        ApiRequest('GET', '/dmt/defaults/keyResources', { setIsProcessing }).then((results) => {
            if (results.data) setMappings(results.data);
        });
    };

    const rowKey = (row) => `${row.key_resource_id}-${row.key_name}-${row.resource_id}-${row.is_setup}`;

    const handleApprove = (row) => {
        const key = rowKey(row);
        setProcessingRows(prev => ({ ...prev, [key]: true }));
        ApiRequest('PATCH', '/dmt/defaults/keyResources', {
            body: {
                keyResourceId: row.key_resource_id,
                keyName: row.key_name,
                resourceId: row.resource_id,
                isSetup: row.is_setup,
                isApproved: row.is_approved ? 0 : 1,
                action: 'approve',
            }
        }).then((results) => {
            setProcessingRows(prev => ({ ...prev, [key]: false }));
            if (results?.data?.success) {
                ToastBuilder('success', `Mapping ${row.is_approved ? 'unapproved' : 'approved'}`);
                loadMappings();
            }
        });
    };

    const handleDelete = (row) => {
        const key = rowKey(row);
        setProcessingRows(prev => ({ ...prev, [key]: true }));
        ApiRequest('PATCH', '/dmt/defaults/keyResources', {
            body: {
                keyResourceId: row.key_resource_id,
                keyName: row.key_name,
                resourceId: row.resource_id,
                isSetup: row.is_setup,
                action: 'delete',
            }
        }).then((results) => {
            setProcessingRows(prev => ({ ...prev, [key]: false }));
            if (results?.data?.success) {
                ToastBuilder('success', 'Mapping deleted');
                loadMappings();
            }
        });
    };

    return (
        <Fragment>
            <Row>
                <Col lg={12}>
                    <Card>
                        <CardHeader className='d-flex justify-content-between align-items-center' style={{ paddingBottom: 0 }}>
                            <div style={{ maxWidth: '80%' }}>
                                <H4 attrH4={{ className: 'card-title mb-0' }}>Key Resource Mappings</H4>
                                <p style={{ fontSize: '13px', fontWeight: 600, marginBottom: 2, marginTop: 4 }}>Where a setup resource's values live inside data records — the bridge between value lists and record fields.</p>
                                <p className='text-muted mb-0' style={{ fontSize: '12px' }}>
                                    Links setup resources to data record keys — defines where a setup resource's values live in the record JSON (e.g. BoardStatuses → <code>status/name</code> in Tickets).
                                    Powers the Mapping Worksheet's Data Recon (record counts), parent-key auto-derivation, and unused-value exclusion in the mapping generator.
                                    Auto-created when key mapping conditions (codes 4/5) are saved. Only approved entries are active via <code>v_dmt_key_resource_lookup</code>. Table: <code>dmt_default_key_resource_mapping</code>
                                </p>
                            </div>
                        </CardHeader>
                        <CardBody style={{ paddingTop: 0 }}>
                            <TableBuilder
                                subHeader={true}
                                columnsToSearch={['key_name', 'key_resource_name', 'setup_resource_name', 'parent_resource_name', 'key_service_code', 'setup_service_code']}
                                data={mappings}
                                processing={isProcessing}
                                pagination={true}
                                columns={[
                                    {
                                        name: <HoverOver content={'Approved = active in v_dmt_key_resource_lookup and consumed by Mapping Worksheet, mapping generator, etc.\nPending = exists but invisible to all features until approved.'} text='Status' />,
                                        selector: (row) => <span className='badge' style={{ backgroundColor: STATUS_COLORS[row.is_approved ? 'Approved' : 'Pending'] }}>
                                            {row.is_approved ? 'Approved' : 'Pending'}
                                        </span>,
                                        sortable: true,
                                        center: true,
                                        maxWidth: '9%',
                                        sortFunction: (a, b) => a.is_approved - b.is_approved,
                                    },
                                    {
                                        name: <HoverOver content={'The parent of the setup resource, if one exists.\n\nExample: "Boards" is the parent of "BoardStatuses" — statuses belong to a board.\nUsed for parent-key auto-derivation in the Mapping Worksheet.'} text='Parent' />,
                                        selector: (row) => row.parent_resource_name || '-',
                                        sortable: true,
                                        maxWidth: '12%',
                                    },
                                    {
                                        name: <HoverOver content={'The setup/value resource whose values this key extracts from data records.\n\nExample: "BoardStatuses" — the resource containing status values like New, Closed, In Progress.'} text='Setup Resource' />,
                                        selector: (row) => <div>
                                            <div>{row.setup_resource_name || ''}</div>
                                            <div className='text-muted' style={{ fontSize: '10px', lineHeight: 1.2 }}>{row.setup_service_code || ''}</div>
                                        </div>,
                                        sortable: true,
                                        sortFunction: (a, b) => (a.setup_resource_name || '').localeCompare(b.setup_resource_name || ''),
                                    },
                                    {
                                        name: <HoverOver content={'The data/migration resource whose records contain the key.\n\nExample: "Tickets" — the actual records being migrated that contain status/name in their JSON.'} text='Data Resource' />,
                                        selector: (row) => <div>
                                            <div>{row.key_resource_name || ''}</div>
                                            <div className='text-muted' style={{ fontSize: '10px', lineHeight: 1.2 }}>{row.key_service_code || ''}</div>
                                        </div>,
                                        sortable: true,
                                        sortFunction: (a, b) => (a.key_resource_name || '').localeCompare(b.key_resource_name || ''),
                                    },
                                    {
                                        name: <HoverOver content={'The JSON key path extracted from data records via JEXTRACT().\n\nExamples:\n• status/name — extracts the status name from ticket records\n• board/id — extracts the board ID for parent grouping\n• type/name — extracts the ticket type'} text='Key' />,
                                        selector: (row) => row.key_name || '-',
                                        sortable: true,
                                    },
                                    {
                                        name: <HoverOver content={'Setup (is_setup=1): Key maps to a value/setup resource — used for value mapping worksheets and record counts.\nCreated from mapping code 4 (Setup Mapping Required).\n\nKey (is_setup=0): Key maps to a record ID reference — used for REC ID lookups.\nCreated from mapping code 5 (REC ID Reference).'} text='Type' />,
                                        selector: (row) => row.is_setup ? 'Setup' : 'Key',
                                        sortable: true,
                                        center: true,
                                        maxWidth: '7%',
                                    },
                                    {
                                        name: 'Actions',
                                        center: true,
                                        maxWidth: '11%',
                                        selector: (row) => (
                                            <ActionGroupBuilder
                                                isApproved={row.is_approved}
                                                disabled={processingRows[rowKey(row)]}
                                                onApprove={() => handleApprove(row)}
                                                onDelete={() => handleDelete(row)}
                                                modalTitle='Key Resource Mapping Details'
                                                modalBody={
                                                    <table className='table table-borderless table-sm mb-0'>
                                                        <tbody>
                                                            <tr><td className='text-muted' style={{width:'40%'}}>Setup Resource</td><td>{row.setup_resource_name} ({row.setup_service_code})</td></tr>
                                                            <tr><td className='text-muted'>Data Resource</td><td>{row.key_resource_name} ({row.key_service_code})</td></tr>
                                                            <tr><td className='text-muted'>Key</td><td>{row.key_name}</td></tr>
                                                            <tr><td className='text-muted'>Type</td><td>{row.is_setup ? 'Setup' : 'Key'}</td></tr>
                                                            <tr><td className='text-muted'>Approved</td><td>{row.is_approved ? 'Yes' : 'No'}</td></tr>
                                                        </tbody>
                                                    </table>
                                                }
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default KeyResourceMappings;
